import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

export default function MediaLightbox({ media, onClose }) {
  useEffect(() => {
    if (!media) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [media, onClose]);

  useEffect(() => {
    if (!media) return;
    const lenis = window.__lenis;
    if (lenis) lenis.stop();
    document.body.style.overflow = "hidden";
    return () => {
      if (lenis) lenis.start();
      document.body.style.overflow = "";
    };
  }, [media]);

  return createPortal(
    <AnimatePresence>
      {media && (
        <div
          key="media-lightbox"
          className="fixed inset-0 z-[80]"
          role="dialog"
          aria-modal="true"
          aria-label={media.label || "Media viewer"}
          data-testid="media-lightbox"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/90"
            data-testid="media-lightbox-backdrop"
          />
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center p-4 sm:p-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 8 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="pointer-events-auto flex max-h-full flex-col items-center"
            >
              {media.type === "video" ? (
                <video
                  src={media.src}
                  poster={media.poster}
                  controls
                  autoPlay
                  playsInline
                  preload="none"
                  className="max-h-[80vh] max-w-[92vw] border border-[#27272A] bg-black sm:max-w-[86vw]"
                  data-testid="media-lightbox-video"
                />
              ) : (
                <img
                  src={media.src}
                  alt={media.alt || ""}
                  className="max-h-[80vh] max-w-[92vw] border border-[#27272A] object-contain sm:max-w-[86vw]"
                  data-testid="media-lightbox-image"
                />
              )}
              {media.label && (
                <p
                  className="mt-4 font-monoalt text-[10px] uppercase tracking-[0.3em] text-zinc-400"
                  data-testid="media-lightbox-caption"
                >
                  {media.label}
                </p>
              )}
            </motion.div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="media-lightbox-close"
            aria-label="Close viewer"
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center border border-[#27272A] text-zinc-400 transition-colors duration-300 hover:border-white/40 hover:text-white sm:right-8 sm:top-8"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
